import { useMemo } from 'react';
import type { DmScanObservation } from './types';
import { getBgColor, getBorderColor, getDotColor } from './dmEventStyles';

interface ObservationsPanelProps {
    observations: DmScanObservation[];
}

const LEVEL_ORDER: Record<DmScanObservation['level'], number> = {
    alert: 0,
    warning: 1,
    'goal-achieved': 2,
    info: 3,
    none: 4
};

function ObservationRow({ observation }: { observation: DmScanObservation }) {
    const dot = getDotColor(observation.level);
    const bg = getBgColor(observation.level);
    const border = getBorderColor(observation.level);
    const highlighted = observation.level === 'alert' || observation.level === 'warning';

    return (
        <li className="flex items-start gap-2 py-1.5">
            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${dot}`} />
            <div
                className={`flex-1 min-w-0 rounded-md px-2 py-1.5 ${
                    highlighted ? `${bg} border-l-[3px] ${border}` : 'bg-gray-50'
                }`}
            >
                {observation.title && (
                    <div className="text-xs text-gray-800 leading-snug">{observation.title}</div>
                )}
                {observation.comments.length > 0 && (
                    <ul className="mt-0.5 flex flex-col gap-0.5">
                        {observation.comments.map((c, i) => (
                            <li
                                key={`${observation.eventId}-${i}`}
                                className="text-[11px] text-gray-500 leading-snug"
                            >
                                {c.title}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </li>
    );
}

function ObservationsPanel({ observations }: ObservationsPanelProps) {
    const sorted = useMemo(
        () => [...observations].sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]),
        [observations]
    );

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 min-h-0 flex-1 flex flex-col">
            <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2 flex-shrink-0">
                Observations
            </h4>

            {sorted.length === 0 ? (
                <div className="text-xs text-gray-400 italic">No observations</div>
            ) : (
                <ul className="flex-1 min-h-0 overflow-y-auto scrollbar-hide -mr-1 pr-1">
                    {sorted.map((o) => (
                        <ObservationRow key={o.eventId} observation={o} />
                    ))}
                </ul>
            )}
        </div>
    );
}

export { ObservationsPanel };
